import { ColumnInterface } from "components/tables/interfaces";
import Table from "components/tables/table";
import { Fragment, FunctionComponent, useState } from "react";
import { useLocales } from "stores/hooks/locale";
import {
  FieldOptionInterface,
  ListEntityField,
} from "models/list-entity-field";
import { parse } from "lib/locale";
import AddFieldOptionModal from "./add-field-option-modal";
import EditFieldOptionModal from "./edit-field-option-modal";
import { StyledLocaleText } from "./field-option-table.styles";

interface FieldOptionsTableProps {
  field: ListEntityField;
  options: FieldOptionInterface[];
  onUpdate: (options: FieldOptionInterface[]) => Promise<void>;
}

interface Row {
  order: number;
  option: FieldOptionInterface;
}

const FieldOptionsTable: FunctionComponent<FieldOptionsTableProps> = ({
  field,
  options = [],
  onUpdate,
}) => {
  const locales = useLocales();
  const [addModal, toggleAddModal] = useState(false);
  const [editModal, toggleEditModal] = useState<
    FieldOptionInterface | undefined
  >(undefined);

  const rows: Row[] = options.map((o, index) => ({
    order: index,
    option: o,
  }));

  const columns: ColumnInterface[] = [
    {
      key: "column",
      header: "Vaihtoehdot",
      render: (row: Row) => {
        const { option } = row;
        return (
          <Fragment>
            {locales.map((loc) => {
              return (
                <StyledLocaleText key={loc}>
                  {parse(option.text, loc)}
                </StyledLocaleText>
              );
            })}
          </Fragment>
        );
      },
    },
  ];

  const handleAdd = async () => toggleAddModal(true);

  const handleDelete = async (row: Row) => {
    const newOptions = options.filter((c) => c.value !== row.option.value);
    onUpdate(newOptions);
  };

  const handleUp = async (row: Row) => {
    const index = options.indexOf(row.option);
    const temp = options[index - 1];
    options[index - 1] = row.option;
    options[index] = temp;
    onUpdate(options);
  };

  const handleDown = async (row: Row) => {
    const index = options.indexOf(row.option);
    const temp = options[index + 1];
    options[index + 1] = row.option;
    options[index] = temp;
    onUpdate(options);
  };

  return (
    <Fragment>
      <Table
        name={`field-${field.uuid}-options`}
        index
        columns={columns}
        data={rows}
        adder={{
          text: "Lisää vaihtoehto",
          onClick: handleAdd,
        }}
        editers={[
          {
            onClick: async (row: Row) => toggleEditModal(row.option),
          },
        ]}
        mover={{
          onUp: handleUp,
          onDown: handleDown,
          isUpDisabled: (row: Row) => row.order === 0,
          isDownDisabled: (row: Row) => row.order === options.length - 1,
        }}
        deleter={{
          onClick: handleDelete,
          isDisabled: () => options.length === 1,
        }}
      />
      {addModal && (
        <AddFieldOptionModal
          options={options}
          onUpdate={async (option: FieldOptionInterface) => {
            // add to the end
            onUpdate([...options, option]);
          }}
          onClose={async () => toggleAddModal(false)}
        />
      )}
      {editModal && (
        <EditFieldOptionModal
          options={options}
          option={editModal}
          onUpdate={async (option: FieldOptionInterface) => {
            const newOptions = options.map((o) => {
              // update values
              if (o.value === editModal.value) {
                o.text = option.text;
                o.value = option.value;
              }
              return o;
            });
            onUpdate(newOptions);
          }}
          onClose={async () => toggleEditModal(undefined)}
        />
      )}
    </Fragment>
  );
};

export default FieldOptionsTable;
